import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { config } from '../../api/config';
import { actions } from '../../state';
import { Button, Paper, Toggle } from '../../components';
import { isEmpty } from '../../utils';
import Form from './banners.form';
import { validationErrors } from './banners.validation';
import {
  createBannerPayload,
  getBannerFieldsFromDiscount,
  getDefaultBannerValues,
  normalizeBannerValues,
} from './banners.utils';
import styles from './banners.module.scss';

const EditBanner = ({
  dispatch,
  history,
  match: { params },
  banners: { selectedBanner, loading, imageUploading },
  discounts: { discounts },
}) => {
  const [values, setValues] = useState(getDefaultBannerValues());

  const fetchBanner = useCallback(() => {
    dispatch(actions.banners.getBannerAction(params.id));
    dispatch(
      actions.discounts.getDiscountsAction({
        limit: 1000,
        offset: 0,
        order: [{ by: 'code', order: 'asc' }],
      })
    );
  }, [dispatch, params.id]);

  useEffect(() => {
    fetchBanner();

    return () => {
      dispatch(actions.banners.clearBannersAction());
    };
  }, [dispatch, fetchBanner]);

  useEffect(() => {
    if (!isEmpty(selectedBanner)) {
      setValues(normalizeBannerValues(selectedBanner));
    }
  }, [selectedBanner]);

  const discountOptions = useMemo(
    () => [
      { name: 'Pasirinkti', value: '' },
      ...discounts.map(discount => ({
        name:
          discount.code ||
          discount.title ||
          discount.description_lt ||
          `#${discount.id}`,
        value: discount.id,
      })),
    ],
    [discounts]
  );

  const selectedDiscount = useMemo(() => {
    const selectedDiscountId = Number(values.discountId);

    if (!selectedDiscountId) return null;

    return (
      discounts.find(discount => Number(discount.id) === selectedDiscountId) ||
      (Number(selectedBanner?.discount?.id) === selectedDiscountId
        ? selectedBanner.discount
        : null)
    );
  }, [discounts, selectedBanner, values.discountId]);
  const discountFieldsLocked = values.hasDiscount && Boolean(selectedDiscount);

  useEffect(() => {
    if (!discountFieldsLocked) return;

    const discountFields = getBannerFieldsFromDiscount(selectedDiscount);

    if (!discountFields) return;

    setValues(currentValues => ({
      ...currentValues,
      ...discountFields,
    }));
  }, [discountFieldsLocked, selectedDiscount]);

  const handleInputChange = ({ target: { name, value } }) => {
    setValues(currentValues => ({
      ...currentValues,
      [name]: value,
    }));
  };

  const handleActivationChange = () => {
    setValues(currentValues => ({
      ...currentValues,
      isActivated: !currentValues.isActivated,
    }));
  };

  const handleDiscountToggle = () => {
    setValues(currentValues => ({
      ...currentValues,
      hasDiscount: !currentValues.hasDiscount,
      discountId: '',
      deepLink: '',
    }));
  };

  const handleLocationToggle = locationValue => {
    setValues(currentValues => {
      const nextPlacements = currentValues.placements.includes(locationValue)
        ? currentValues.placements.filter(
            location => location !== locationValue
          )
        : [...currentValues.placements, locationValue];

      return {
        ...currentValues,
        placements: nextPlacements,
      };
    });
  };

  const handleFileUploaded = file => {
    if (!file) return;

    const imageUrl = URL.createObjectURL(file);
    setValues(currentValues => ({
      ...currentValues,
      image: {
        name: file.name,
        url: imageUrl,
      },
    }));
    dispatch(actions.banners.uploadBannerImageAction(file));
  };

  const handleFileDeleted = () => {
    setValues(currentValues => ({
      ...currentValues,
      image: null,
    }));
    dispatch(actions.banners.uploadBannerImageAction(null));
  };

  const handleSubmit = event => {
    event.preventDefault();

    const error = validationErrors(values);

    if (error) {
      dispatch(actions.error.setErrorAction({ msg: error }));
      return;
    }

    dispatch(
      actions.banners.editBannerAction(
        params.id,
        createBannerPayload(values),
        history
      )
    );
  };

  const linkedDiscountUrl =
    selectedBanner?.id && values.hasDiscount && values.discountId
      ? config.APP_PATHS.editDiscount(values.discountId)
      : null;

  return (
    <section className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Banerio kūrimas</h1>
        <div className={styles.pageHeaderActions}>
          {linkedDiscountUrl && (
            <Button
              color="light"
              url={linkedDiscountUrl}
              classes={{ button: styles.linkedDiscountButton }}
            >
              Susijusi nuolaida
            </Button>
          )}
          <div className={styles.toggleMeta}>
            <Toggle
              checked={values.isActivated}
              onChange={handleActivationChange}
            />
            <span>Aktyvuota</span>
          </div>
        </div>
      </div>

      <Paper size="sm" className={styles.card}>
        <Form
          values={values}
          onChange={handleInputChange}
          onSubmit={handleSubmit}
          onToggleDiscount={handleDiscountToggle}
          onLocationToggle={handleLocationToggle}
          onFileUploaded={handleFileUploaded}
          onFileDeleted={handleFileDeleted}
          discountOptions={discountOptions}
          discountFieldsLocked={discountFieldsLocked}
          loading={loading}
          imageUploading={imageUploading}
          dateToMin={null}
        />
      </Paper>
    </section>
  );
};

const mapStateToProps = state => ({
  banners: state.banners,
  discounts: state.discounts,
});

EditBanner.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  banners: PropTypes.shape({
    selectedBanner: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired,
    imageUploading: PropTypes.bool.isRequired,
  }).isRequired,
  discounts: PropTypes.shape({
    discounts: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
};

export default connect(mapStateToProps)(EditBanner);
